import { supabase } from '@/integrations/supabase/client';

// Available models configuration - centralized source of truth
export const AVAILABLE_MODELS = {
  'meta-llama/llama-4-maverick-17b-128e-instruct-fp8': {
    name: 'Llama 4 Maverick',
    displayName: 'Llama 4',
    provider: 'Meta/Novita',
    description: 'High-performance financial AI model with advanced reasoning capabilities',
    badge: 'Professional',
    badgeColor: 'bg-blue-500',
    apiKey: import.meta.env.VITE_NOVITA_API_KEY || ''
  },
  'deepseek/deepseek-v3-0324': {
    name: 'DeepSeek V3',
    displayName: 'DeepSeek V3', 
    provider: 'DeepSeek/Novita',
    description: 'Latest generation model with enhanced reasoning and coding capabilities',
    badge: 'Advanced',
    badgeColor: 'bg-purple-500',
    apiKey: import.meta.env.VITE_NOVITA_API_KEY || ''
  }
} as const;

export type ModelId = keyof typeof AVAILABLE_MODELS;

interface ModelConfig {
  modelId: string;
  apiKey: string;
  displayName: string;
  badge: string;
  temperature: number;
}

class ModelConfigService {
  private currentConfig: ModelConfig | null = null;
  private listeners: Array<(config: ModelConfig) => void> = [];

  // Get current model configuration from database
  async getCurrentConfig(): Promise<ModelConfig> {
    if (this.currentConfig) {
      return this.currentConfig;
    }

    try {
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['selected_model', 'current_api_key', 'llm_temperature']);

      if (error) throw error;

      // Parse settings
      const settingsMap = data?.reduce((acc, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {} as Record<string, string>) || {};

      // Get model ID with fallback
      const modelId = settingsMap.selected_model || 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8';
      const modelConfig = AVAILABLE_MODELS[modelId as ModelId];
      
      if (!modelConfig) {
        throw new Error(`Unknown model: ${modelId}`);
      }

      // Build configuration
      const config: ModelConfig = {
        modelId,
        apiKey: settingsMap.current_api_key || modelConfig.apiKey,
        displayName: modelConfig.displayName,
        badge: modelConfig.badge,
        temperature: parseFloat(settingsMap.llm_temperature) || 0.2
      };

      this.currentConfig = config;
      this.notifyListeners(config);
      
      return config;
    } catch (error) {
      console.error('Error loading model config:', error);
      
      // Return default configuration on error
      const defaultConfig: ModelConfig = {
        modelId: 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8',
        apiKey: AVAILABLE_MODELS['meta-llama/llama-4-maverick-17b-128e-instruct-fp8'].apiKey,
        displayName: 'Llama 4',
        badge: 'Professional',
        temperature: 0.2
      };

      this.currentConfig = defaultConfig;
      return defaultConfig;
    }
  }

  // Get display name for a model ID
  getModelDisplayName(modelId?: string): string {
    if (!modelId) return 'Stewie AI';
    
    if (modelId.includes('llama-4') || modelId.includes('llama4')) {
      return 'Llama 4';
    } else if (modelId.includes('deepseek')) {
      return 'DeepSeek V3';
    } else if (modelId.includes('claude')) {
      return 'Claude (Fallback)';
    }
    
    return modelId.split('/').pop() || modelId;
  }

  // Get badge display name (shorter version for UI)
  getBadgeDisplayName(modelId?: string): string {
    if (!modelId) return 'AI';
    
    const config = AVAILABLE_MODELS[modelId as ModelId];
    return config?.displayName || this.getModelDisplayName(modelId);
  }

  // Subscribe to configuration changes
  subscribe(listener: (config: ModelConfig) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Force refresh of configuration (useful after admin changes)
  async refreshConfig(): Promise<ModelConfig> {
    this.currentConfig = null;
    return this.getCurrentConfig();
  }

  // Clear cached configuration
  clearCache(): void {
    this.currentConfig = null;
  }

  private notifyListeners(config: ModelConfig): void {
    this.listeners.forEach(listener => {
      try {
        listener(config);
      } catch (error) {
        console.error('Error in model config listener:', error);
      }
    });
  }
}

// Export singleton instance
export const modelConfigService = new ModelConfigService();

// Utility function to get current model configuration (non-React)
export const getCurrentModelConfig = async (): Promise<ModelConfig> => {
  return modelConfigService.getCurrentConfig();
};

// Utility function to get model display name
export const getModelDisplayName = (modelId?: string): string => {
  return modelConfigService.getModelDisplayName(modelId);
};

// Utility function to get badge display name
export const getBadgeDisplayName = (modelId?: string): string => {
  return modelConfigService.getBadgeDisplayName(modelId);
}; 